import React, {Key, useContext, useEffect, useRef, useState} from "react";
import PagedSearchTable, {PagedTableFunctions} from "../../components/PagedSearchTable";
import FarmController from "../../generated/controllers/farmController";
import BlockRowDisplay from "../../generated/interfaces/blockRowDisplay";
import BlockAnomaly from "../../generated/interfaces/blockAnomaly";
import PaginationResponse from "../../generated/interfaces/paginationResponse";
import PaginationRequestSearch from "../../generated/interfaces/paginationRequestSearch";
import {PaginationRequestSearchEx} from "../../generated/request";
import {dateFormat} from "../../date";
import AppContext from "../appContext";
import Success from "../../components/Success";
import Failed from "../../components/Failed";
import Dialog from "../../components/Dialog";

// Ready for Processing: review each block's inputs + anomaly, approve, then mark ready so the yield
// cron regenerates the farm's yield COG. Approve is required before Ready (enforced server-side).
// "Check anomalies" runs the SOW factor + cap check for every block at once (cancellable), and shows
// the results in a dialog.
const ReadyForProcessingTab: React.FC<{ farmId: number; seasonId: number | null }> = (props) => {
    const context = useContext(AppContext)
    const pagedRef = useRef<PagedTableFunctions<BlockRowDisplay>>()
    const [selected, setSelected] = useState<Key[]>([])
    // Approved-state of loaded rows, so "Mark Ready" can be gated on the whole selection being approved.
    const [rowsById, setRowsById] = useState<Map<number, BlockRowDisplay>>(new Map())
    // Latest anomaly result per block (from "Check anomalies"), rendered in the Anomaly column.
    const [anomalies, setAnomalies] = useState<Map<number, BlockAnomaly>>(new Map())
    // Anomaly dialog: loading -> results (or error). All three states live in the same dialog.
    const [checking, setChecking] = useState(false)
    const [resultList, setResultList] = useState<BlockAnomaly[] | null>(null)
    const [errorMsg, setErrorMsg] = useState<string | null>(null)
    const abortRef = useRef<AbortController | null>(null)

    function search(request: PaginationRequestSearchEx<BlockRowDisplay>) {
        return FarmController.blocksForEdit({...request, id: props.farmId, seasonId: props.seasonId}).then(resp => {
            setRowsById(prev => {
                const next = new Map(prev)
                resp.items.forEach(r => next.set(r.landId, r))
                return next
            })
            return resp
        })
    }

    useEffect(() => {
        setSelected([])
        setRowsById(new Map())
        setAnomalies(new Map())
        setResultList(null)
        setErrorMsg(null)
        pagedRef.current?.refresh()
    }, [props.farmId, props.seasonId])

    // A block can only be marked ready once approved (also enforced server-side); disable the action
    // until every selected block is approved.
    const canMarkReady = selected.length > 0 && selected.every(k => rowsById.get(Number(k))?.approved === true)

    // Refresh the rows to reflect the new state but KEEP the current selection, so the user can chain
    // actions on the same blocks (e.g. Approve, then Mark Ready) without re-selecting.
    function reloadKeepingSelection() {
        pagedRef.current?.refresh()
    }

    function setApproval(state: boolean) {
        const landIds = selected.map(k => Number(k))
        if (landIds.length === 0) return
        FarmController.setBlockApproval({id: props.farmId, seasonId: props.seasonId, landIds, state}).then(() => {
            context.showSnack(<Success title='Success' text={`${landIds.length} block(s) ${state ? 'approved' : 'un-approved'}.`}/>)
            reloadKeepingSelection()
        }).catch(() => context.showSnack(<Failed title='Failed' text='Could not update approval.'/>))
    }

    function setReady(state: boolean) {
        const landIds = selected.map(k => Number(k))
        if (landIds.length === 0) return
        FarmController.setBlockReady({id: props.farmId, seasonId: props.seasonId, landIds, state}).then(() => {
            context.showSnack(<Success title='Success' text={`${landIds.length} block(s) ${state ? 'marked ready' : 'unmarked'}.`}/>)
            reloadKeepingSelection()
        }).catch(() => context.showSnack(<Failed title='Failed' text='Could not update ready state.'/>))
    }

    // Run the factor + cap anomaly check for every weighted block on the farm. Uses a raw fetch (the
    // generated client can't pass an abort signal) so Cancel aborts the request AND, because the
    // endpoint honours the cancellation token, stops the server work between blocks. Results are shown
    // in the same dialog (no snack).
    function checkAll() {
        const controller = new AbortController()
        abortRef.current = controller
        setResultList(null)
        setErrorMsg(null)
        setChecking(true)
        fetch('/api/farm/checkfarmanomalies', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            credentials: 'same-origin',
            signal: controller.signal,
            body: JSON.stringify({id: props.farmId, seasonId: props.seasonId})
        })
            .then(res => {
                if (!res.ok) throw new Error(res.statusText)
                return res.json() as Promise<BlockAnomaly[]>
            })
            .then(results => {
                setAnomalies(new Map(results.map(a => [a.landId, a])))
                setResultList(results)
            })
            .catch((e: unknown) => {
                if (e instanceof DOMException && e.name === 'AbortError') return // user cancelled
                setErrorMsg('Could not check anomalies.')
            })
            .finally(() => {
                setChecking(false)
                abortRef.current = null
            })
    }

    function cancelCheck() {
        abortRef.current?.abort()
        setChecking(false)
        setResultList(null)
        setErrorMsg(null)
    }

    function closeDialog() {
        setResultList(null)
        setErrorMsg(null)
    }

    function statusText(row: BlockRowDisplay): string {
        if (!row.readyForProcessing) return row.approved ? 'Approved' : 'Not ready'
        if (!row.lastProcessedAt) return 'Waiting'
        // A run that recorded an error produced no raster - surface it as Failed, not Processed.
        return row.lastError ? 'Failed' : 'Processed'
    }

    function anomalyBadge(a: BlockAnomaly) {
        // Blocks that can't be checked (no weight, no crop, no picks, missing brake-point) show the reason.
        if (a.error) return <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800" title={a.error}>{a.error}</span>
        if (a.capExceeded) return <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">⚠ Expected ≥ cap</span>
        return a.isAnomaly
            ? <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">⚠ Factor jump</span>
            : <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">✓ ok</span>
    }

    // Client-side paging of the in-memory anomaly results, so the dialog uses the same PagedSearchTable
    // (styling + search + pagination) as the rest of the app.
    function anomalyPage(request: PaginationRequestSearch): PaginationResponse<BlockAnomaly> {
        const list = resultList ?? []
        const term = (request.search ?? '').toLowerCase()
        const filtered = term ? list.filter(a => a.landName.toLowerCase().includes(term)) : list
        const start = request.page * request.rowsPerPage
        return {total: filtered.length, items: filtered.slice(start, start + request.rowsPerPage)}
    }

    function factorDeltaCell(a: BlockAnomaly) {
        if (a.error || a.factorChangePct == null) return <span className="text-gray-400">—</span>
        return <span className={a.factorJump ? 'text-red-700 font-medium' : ''}>{`${a.factorChangePct > 0 ? '+' : ''}${a.factorChangePct.toFixed(0)}%`}</span>
    }

    function anomalyCell(r: BlockRowDisplay) {
        const a = anomalies.get(r.landId)
        if (a) return anomalyBadge(a)
        // No computed result yet - still surface the input-only cap flag.
        if (r.capExceeded) return <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">⚠ Expected ≥ cap</span>
        return <span className="text-gray-400">—</span>
    }

    const flaggedCount = resultList ? resultList.filter(a => a.isAnomaly).length : 0
    const notCheckedCount = resultList ? resultList.filter(a => !!a.error).length : 0

    return (
        <div className="mx-2 mt-3">
            <div className="mb-2">
                <button className="btn btn-sm btn-primary" onClick={checkAll} title="Runs the factor + cap check for every block.">
                    Check anomalies
                </button>
            </div>

            {selected.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2 mb-2 bg-gray-100 rounded px-3 py-2">
                    <span className="text-sm text-gray-600">{selected.length} selected</span>
                    <div className="btn-sm btn-primary" onClick={() => setApproval(true)}>Approve</div>
                    <div className="btn-sm bg-gray-400 text-white rounded px-2 py-1 cursor-pointer" onClick={() => setApproval(false)}>Un-approve</div>
                    <div
                        className={`btn-sm ${canMarkReady ? 'btn-primary' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                        title="You can only mark approved blocks as ready."
                        onClick={() => canMarkReady && setReady(true)}>
                        Mark Ready
                    </div>
                    <div className="btn-sm bg-gray-400 text-white rounded px-2 py-1 cursor-pointer" onClick={() => setReady(false)}>Unmark</div>
                </div>
            ) : null}

            <PagedSearchTable<BlockRowDisplay>
                componentRef={pagedRef}
                call={w => search({...w, sortBy: 'lastUpdated', ascending: false})}
                keyExtractor={row => row.landId}
                selection={{selectedKeys: selected, onSelectionChange: setSelected}}
                isSelectable={row => row.value != null}
                unselectableTooltip="No weight entered for this block."
                columns={[
                    {header: 'Block', key: 'name', row: r => r.name},
                    {header: 'Crop / Cultivar', key: 'crop', row: r => `${r.cropName ?? '-'}${r.cultivarName ? ' / ' + r.cultivarName : ''}`},
                    {header: 'Weight (KG)', key: 'value', row: r => r.value != null ? r.value : '-'},
                    {header: 'Expected T/ha', key: 'expected', row: r => r.expectedTha != null ? r.expectedTha.toFixed(2) : '-'},
                    {header: 'Cap', key: 'cap', row: r => r.capTha != null ? r.capTha.toFixed(0) : '-'},
                    {header: 'Anomaly', key: 'anomaly', row: anomalyCell},
                    {header: 'Approved', key: 'approved', row: r => r.approved ? '✓' : '-'},
                    {header: 'Last Factor', key: 'factor', row: r => r.lastFactor != null ? r.lastFactor.toFixed(3) : '-'},
                    {header: 'Last Processed', key: 'processed', row: r => r.lastProcessedAt ? dateFormat(r.lastProcessedAt, '%d %M %Y %H:%i') : '-'},
                    {header: 'Status', key: 'status', row: r => {
                        const s = statusText(r)
                        return s === 'Failed'
                            ? <span className="text-red-700" title={r.lastError ?? undefined}>{s}</span>
                            : s
                    }},
                ]}/>

            <Dialog
                show={checking || resultList != null || errorMsg != null}
                title="Anomaly check"
                body={
                    checking
                        ? <div className="p-4 min-w-[280px] text-center text-gray-700">
                            <div>Running calculations for all blocks… This may take a while.</div>
                        </div>
                        : errorMsg
                            ? <div className="p-4 min-w-[280px] text-red-700">{errorMsg}</div>
                            : <div className="p-3 min-w-[640px]">
                                <div className="mb-2 text-sm text-gray-600">
                                    {flaggedCount} flagged{notCheckedCount > 0 ? `, ${notCheckedCount} could not be checked` : ''} of {resultList?.length ?? 0} block(s).
                                </div>
                                <PagedSearchTable<BlockAnomaly>
                                    call={anomalyPage}
                                    keyExtractor={a => a.landId}
                                    columns={[
                                        {header: 'Block', key: 'block', row: a => a.landName},
                                        {header: 'Factor', key: 'factor', row: a => a.error ? '-' : a.factor.toFixed(3)},
                                        {header: 'Δ vs last', key: 'delta', row: factorDeltaCell},
                                        {header: 'Status', key: 'status', row: anomalyBadge},
                                    ]}/>
                            </div>
                }
                footer={
                    checking
                        ? <button className="btn btn-sm btn-error" onClick={cancelCheck}>Cancel</button>
                        : <button className="btn btn-sm btn-primary" onClick={closeDialog}>Close</button>
                }
            />
        </div>
    )
}

export default ReadyForProcessingTab
